import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { formatImageUrl } from '../utils/imageUtils';
import { API_BASE_URL } from '../config';
import {
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  ClockIcon,
  ShareIcon
} from '@heroicons/react/24/outline';

interface Speaker {
  id: string;
  name: string;
  display_order: number;
  image?: string;
  company?: string;
}

interface Session {
  id: string;
  title: string;
  description: string;
  type: string;
  speakers: Speaker[];
  startTime: string;
  endTime: string;
  room: string;
  status: string;
}

interface BadgeTheme {
  name: string;
  background: string;
  textColor: string;
  accentColor: string;
  secondaryColor: string;
}

const themes: { [key: string]: BadgeTheme } = {
  default: {
    name: 'Conference Blue',
    background: 'linear-gradient(135deg, #00529B 0%, #003D74 100%)',
    textColor: '#ffffff',
    accentColor: '#E31D3C',
    secondaryColor: '#f8fafc'
  },
  gradient: {
    name: 'Gradient',
    background: 'linear-gradient(135deg, #00529B 0%, #E31D3C 100%)',
    textColor: '#ffffff',
    accentColor: '#ffffff',
    secondaryColor: '#f1f5f9'
  },
  minimal: {
    name: 'Minimal',
    background: '#ffffff',
    textColor: '#1f2937',
    accentColor: '#00529B',
    secondaryColor: '#f9fafb'
  },
  dark: {
    name: 'Dark',
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    textColor: '#ffffff',
    accentColor: '#E31D3C',
    secondaryColor: '#374151'
  },
  keynote: {
    name: 'Keynote Gold',
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    textColor: '#ffffff',
    accentColor: '#fbbf24',
    secondaryColor: '#fef3c7'
  }
};

const SessionBadge: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // URL Parameters
  const theme = searchParams.get('theme') || 'default';
  const size = searchParams.get('size') || 'large'; // small, medium, large
  const format = searchParams.get('format') || 'card'; // card, banner, square, story
  const showSpeakers = searchParams.get('speakers') !== 'false';
  const showTime = searchParams.get('time') !== 'false';
  const showRoom = searchParams.get('room') === 'true'; // Changed default to false
  const customTitle = searchParams.get('title');
  const customText = searchParams.get('text');
  const watermark = searchParams.get('watermark') !== 'false';

  useEffect(() => {
    const fetchSession = async () => {
      if (!sessionId) {
        setError('Session ID is required');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/public/sessions`);
        const data = await response.json();
        const foundSession = data.sessions.find((s: Session) => s.id === sessionId);
        
        if (!foundSession) {
          setError('Session not found');
        } else {
          setSession(foundSession);
        }
      } catch (err) {
        setError('Failed to fetch session data');
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId]);

  const formatTime = (timeString: string): string => {
    return new Date(timeString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (timeString: string): string => {
    return new Date(timeString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSessionTypeColor = (type: string, currentTheme: BadgeTheme): string => {
    if (currentTheme.name === 'Minimal') {
      return 'bg-gray-100 text-gray-800';
    }
    return `bg-white bg-opacity-20 text-white`;
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return {
          container: 'w-80 h-64', // Increased height for more content
          title: 'text-base',
          text: 'text-xs',
          speaker: 'text-xs',
          avatar: 'w-5 h-5',
          padding: 'p-3'
        };
      case 'medium':
        return {
          container: 'w-96 h-80', // Increased height for more content
          title: 'text-lg',
          text: 'text-sm',
          speaker: 'text-xs',
          avatar: 'w-6 h-6',
          padding: 'p-4'
        };
      case 'large':
      default:
        return {
          container: 'w-[600px] h-96', // Increased height for more content
          title: 'text-2xl',
          text: 'text-base',
          speaker: 'text-sm',
          avatar: 'w-8 h-8',
          padding: 'p-6'
        };
    }
  };

  const getFormatClasses = () => {
    switch (format) {
      case 'banner':
        return 'w-[800px] h-48';
      case 'square':
        return 'w-96 h-96';
      case 'story':
        return 'w-80 h-[600px]';
      case 'card':
      default:
        return getSizeClasses().container;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white rounded-lg p-8 shadow-lg text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600">{error || 'Session not found'}</p>
        </div>
      </div>
    );
  }

  const currentTheme = themes[theme] || themes.default;
  const sizeClasses = getSizeClasses();
  const containerClass = getFormatClasses();

  const displayTitle = customTitle || session.title;
  const displayDescription = customText || session.description;

  return (
    <div 
      id="badge-container"
      className={`${containerClass} ${sizeClasses.padding} rounded-xl shadow-2xl relative overflow-hidden flex flex-col justify-between`}
      style={{ 
        background: currentTheme.background,
        color: currentTheme.textColor,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        textRendering: 'optimizeLegibility',
        boxSizing: 'border-box'
      }}
    >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white transform translate-x-16 -translate-y-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white transform -translate-x-12 translate-y-12"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex-1" style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <span 
              className={`px-3 py-1 rounded-full text-xs font-medium ${getSessionTypeColor(session.type, currentTheme)}`}
            >
              {session.type.toUpperCase()}
            </span>
            
            {(showTime || showRoom) && (
              <div className="text-right text-xs opacity-90">
                {showTime && (
                  <div className="flex items-center justify-end mb-1">
                    <CalendarIcon className="w-3 h-3 mr-1" />
                    {formatDate(session.startTime)}
                  </div>
                )}
                {showTime && (
                  <div className="flex items-center justify-end mb-1">
                    <ClockIcon className="w-3 h-3 mr-1" />
                    {formatTime(session.startTime)} - {formatTime(session.endTime)}
                  </div>
                )}
                {showRoom && (
                  <div className="flex items-center justify-end">
                    <MapPinIcon className="w-3 h-3 mr-1" />
                    Room {session.room}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className={`${sizeClasses.title} font-bold mb-3`} style={{ 
            lineHeight: '1.2',
            margin: '0 0 12px 0',
            padding: '0',
            verticalAlign: 'baseline'
          }}>
            {displayTitle}
          </h1>

          {/* Description */}
          <p className={`${sizeClasses.text} opacity-90 mb-4`} style={{ 
            lineHeight: '1.4',
            margin: '0 0 16px 0',
            padding: '0',
            verticalAlign: 'baseline'
          }}>
            {displayDescription.length > 120 
              ? displayDescription.substring(0, 120) + '...' 
              : displayDescription
            }
          </p>

          {/* Speakers */}
          {showSpeakers && session.speakers && session.speakers.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center mb-2">
                <UserIcon className="w-4 h-4 mr-1" />
                <span className={`${sizeClasses.speaker} font-medium opacity-90`}>
                  {session.speakers.length === 1 ? 'Speaker' : 'Speakers'}
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {session.speakers
                  .sort((a, b) => a.display_order - b.display_order)
                  .map(speaker => (
                    <div key={speaker.id} className="flex items-center space-x-1 bg-white bg-opacity-20 rounded-lg p-1">
                      <img
                        src={formatImageUrl(speaker.image, "/placeholder-speaker.svg")}
                        alt={speaker.name}
                        className={`${sizeClasses.avatar} rounded-full object-cover border border-white border-opacity-30`}
                      />
                      <div className="min-w-0">
                        <p className={`${sizeClasses.speaker} font-medium truncate`} style={{ lineHeight: '1.3' }}>
                          {speaker.name}
                        </p>
                        {speaker.company && size !== 'small' && (
                          <p className="text-xs opacity-75 truncate" style={{ lineHeight: '1.2' }}>
                            {speaker.company}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {watermark && (
          <div className="relative z-10 flex items-center justify-between pt-4 border-t border-white border-opacity-20">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <ShareIcon className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium opacity-90">
                Language Intelligence 2025
              </span>
            </div>
            <div className="text-xs opacity-75">
              language-intelligence.com
            </div>
          </div>
        )}

        {/* Download/Share Controls (only visible in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="fixed top-4 right-4 bg-white rounded-lg shadow-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm text-gray-800">Badge Controls</h3>
            <div className="text-xs text-gray-600 space-y-1">
              <div>Theme: {currentTheme.name}</div>
              <div>Size: {size}</div>
              <div>Format: {format}</div>
            </div>
          </div>
        )}
      </div>
  );
};

export default SessionBadge;
